import type { DetailedHTMLProps, HTMLAttributes } from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';

import type { IconAnimation, IconName, IconSize, IconVariant } from './types';
import { getAnimationClassName } from './utils/getAnimationClassName';

export interface IconProps
  extends ComponentProps<
    Omit<
      DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
      'children'
    >
  > {
  type: IconName;
  variant?: IconVariant;
  /**
   * @see https://docs.fontawesome.com/web/style/size/
   *
   * @example
   * fa-2xs // 0.625em - 10px
   * fa-xs 	// 0.75em  - 12px
   * fa-sm	// 0.875em - 14px
   * fa-lg	// 1.25em  - 20px
   * fa-xl	// 1.5em   - 24px
   * fa-2xl	// 2em     - 32px
   */
  size?: IconSize;
  animation?: IconAnimation;
}

export function Icon({
  size = 'lg',
  type,
  variant = 'solid',
  animation,
  ...props
}: IconProps) {
  const fullVariant = `fa-${variant}`;
  const fullName = `fa-${type}`;
  const fullSize = `fa-${size}`;
  const fullAnimations = getAnimationClassName(animation);
  const overrideClassName = getClassNameOverride(props, 'Icon');

  return (
    <i
      role="img"
      {...props}
      className={cn(
        fullVariant,
        fullName,
        fullSize,
        fullAnimations,
        overrideClassName,
      )}
    />
  );
}

Icon.displayName = 'Icon';
